import type {
  FormatRoutineSelectLabelOptions,
  GetRouteNamesForRoutineOptions,
  GetSelectedRouteRoutineByIdOptions,
  MapScheduleRoutinesToSelectOptionsParams,
  ScheduleRouteRoutineSelectOption,
  SelectedScheduleRouteRoutine,
} from '../types/MachineDetailsRobotSchedule.types';
import type { RobotScheduleRoutineSelectOption } from '../types/RobotScheduleRoutine.types';
import type { RobotScheduleRoute, RobotScheduleRouteSelectOption } from '../types/RobotScheduleRoute.types';
import type { ScheduleOccurrence } from 'app/modules/machine-inventory/ScheduleClient';

export const getRouteNamesForRoutine = ({ routine, routes }: GetRouteNamesForRoutineOptions): string[] => {
  const routesByRobotRouteId = new Map(routes.map(route => [route.robotRouteId, route]));

  return routine.routeIds
    .map(routeId => routesByRobotRouteId.get(routeId)?.name)
    .filter((routeName): routeName is string => Boolean(routeName));
};

export const formatRoutineSelectLabel = ({
  routine,
  routes,
  unnamedRoutineFallback,
}: FormatRoutineSelectLabelOptions): string => {
  const routineName = routine.name?.trim();
  const routeNames = getRouteNamesForRoutine({ routine, routes });

  // An anonymous routine with no name and exactly one route is effectively just that route —
  // show the route's own name instead of a generic fallback (and don't repeat it in parentheses).
  if (!routineName && routine.anonymous && routine.routeIds.length === 1 && routeNames.length === 1) {
    return routeNames[0];
  }

  const displayName = routineName || unnamedRoutineFallback;

  if (routeNames.length === 0) {
    return displayName;
  }

  return `${displayName} (${routeNames.join(', ')})`;
};

export const getSelectedRouteRoutineFromSelectOption = (option: unknown): SelectedScheduleRouteRoutine | undefined => {
  if (!option || typeof option !== 'object') {
    return undefined;
  }

  const selectOption = option as ScheduleRouteRoutineSelectOption;

  if (selectOption.routine) {
    return {
      type: 'routine',
      routine: {
        ...selectOption.routine,
        name: selectOption.searchLabel,
      },
    };
  }

  if (selectOption.route) {
    return {
      type: 'route',
      route: {
        ...selectOption.route,
        name: selectOption.searchLabel,
      },
    };
  }

  return undefined;
};

export const getSelectedRouteRoutineById = ({
  id,
  routines,
  routes,
  unnamedRoutineFallback,
}: GetSelectedRouteRoutineByIdOptions): SelectedScheduleRouteRoutine | undefined => {
  const routine = routines.find(item => item.id === id);

  if (routine) {
    return {
      type: 'routine',
      routine: {
        ...routine,
        name: formatRoutineSelectLabel({ routine, routes, unnamedRoutineFallback }),
      },
    };
  }

  const route = routes.find(item => item.id === id);

  if (route) {
    return {
      type: 'route',
      route,
    };
  }

  return undefined;
};

export const mapScheduleRoutinesToSelectOptions = ({
  routines,
  routes,
  unnamedRoutineFallback,
}: MapScheduleRoutinesToSelectOptionsParams): RobotScheduleRoutineSelectOption[] =>
  routines.map(routine => ({
    label: formatRoutineSelectLabel({ routine, routes, unnamedRoutineFallback }),
    value: routine.id,
    routine,
  }));

export const mapScheduleRoutesToSelectOptions = (routes: RobotScheduleRoute[]): RobotScheduleRouteSelectOption[] =>
  routes.map(route => ({
    label: route.name,
    value: route.id,
    route,
  }));

/**
 * cleaningScheduleOccurrences doesn't return the source schedule's route/routine picker options
 * (no getSchedule API yet), so looking a real occurrence's route/routine up in the fetched
 * routes/routines list can miss. Builds a selection directly from what the occurrence itself
 * returns instead.
 * TODO: remove once create/edit/delete reads the source schedule's route/routine directly.
 */
export const buildFallbackRouteRoutineSelection = (
  occurrence: ScheduleOccurrence
): SelectedScheduleRouteRoutine | undefined => {
  if (occurrence.routine) {
    return {
      type: 'routine',
      routine: {
        id: occurrence.routine.id,
        name: occurrence.routine.name,
        duration: occurrence.routine.duration,
        postChargingDuration: occurrence.routine.postChargingDuration,
        anonymous: occurrence.routine.anonymous,
        routeIds: occurrence.routine.routeIds,
      },
    };
  }

  if (occurrence.route) {
    return {
      type: 'route',
      route: {
        id: occurrence.route.id,
        name: occurrence.route.name,
        duration: occurrence.route.duration,
        postChargingDuration: occurrence.route.postChargingDuration,
        robotRouteId: 0,
      },
    };
  }

  return undefined;
};
